import { useEffect } from "react";
import { useSessionRunsContext } from "../../contexts/sessionRunsContext";
import {
  ControlSessionStatusResponse,
  hasActiveLifecycleState,
} from "../../models/audit/controlSession";

const POLL_INTERVAL_MS = 5000;

/**
 * Sibling poller to `useControlSessionStatus`. While at least one procedure
 * session has a lifecycle field that is not yet `ready`, refreshes the
 * `SessionRunsProvider` context every {@link POLL_INTERVAL_MS} by calling
 * `fetchSessionRuns(controlSessionId)`.
 *
 * Mount only inside a `<SessionRunsProvider>` subtree (i.e., the work-paper
 * view shell). Keeping the polling separate from `useControlSessionStatus`
 * lets that hook stay mountable on pages that sit outside the
 * `SessionRunsProvider` (AuditSessionPageV2, AuditDocumentsPageV2).
 *
 * Pass the `data` returned from `useControlSessionStatus(controlSessionId)`
 * so the same lifecycle snapshot drives both the status invalidations and
 * the session-runs gating.
 */
export function useSessionRunsPolling(
  controlSessionId: string | undefined,
  statusData: ControlSessionStatusResponse | undefined,
): void {
  const { fetchSessionRuns } = useSessionRunsContext();

  useEffect(() => {
    if (!controlSessionId || !statusData) return;
    // Active check (not `!== "ready"`): the degraded "unknown" lifecycle
    // sentinel counts as settled, so an unrecognized backend value stops
    // this poll instead of driving it forever.
    const anyNotReady = statusData.procedureSessions.some(
      hasActiveLifecycleState,
    );
    if (!anyNotReady) return;
    const id = setInterval(() => {
      void fetchSessionRuns(controlSessionId);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [controlSessionId, statusData, fetchSessionRuns]);
}
